import { stripSensitiveFields } from 'utils/strip-sensitive-fields';
import { isIndexableTrainerProfile } from 'utils/trainer-employment';
import React from 'react'
import Head from 'next/head'
import { useRouter } from 'next/router'
import Script from 'next/script'
import { Review } from '../../../../components/profile/Review'
import { downloadAbout, downloadProfilePic, downloadRating, getStringUserIdFromHandleName } from '../../../../api/user/profile-api'

// This gets called on every request
export async function getServerSideProps({params, res}) {
  // Fetch data from external API
  const handleName = params.handleName
  let stringsUserId, about
  try {
    stringsUserId = (await getStringUserIdFromHandleName(handleName)).toString()
    about = stripSensitiveFields(await downloadAbout("", stringsUserId))
  } catch {
    return { notFound: true }
  }
  if (!about?.userFirstName && !about?.userLastName) return { notFound: true }
  const profilePicture = await downloadProfilePic(stringsUserId)
  const reviews = await downloadRating(0, stringsUserId, 0, 20)

  res.setHeader(
    'Cache-Control',
    'public, s-maxage=86400, stale-while-revalidate=59'
  )

  return {
    props: {
      handleName,
      stringsUserId,
      reviews,
      about,
      profilePicture
    },
  }
}

export default function Home(props) {
  const router = useRouter()
  const stringHandleName = props.handleName
  const stringsUserId = props.stringsUserId

  const about = props.about
  const profilePicture = props.profilePicture
  const reviews = props.reviews

  const fullName = [about.userFirstName, about.userLastName].filter(Boolean).join(" ")
  // Reviews can be left without a completed job (RateService submits job 0),
  // so this page is only indexable for profiles the sitemap would list.
  const isIndexable = about.category === "Fitness Trainer" && isIndexableTrainerProfile(about)

  let description = "Reviews for " + about.userFirstName + " " + about.userLastName + " - " + about.category

  return (
    <>
      <Head>
        <title>{`Reviews for ${about.userFirstName} ${about.userLastName} | ${about.category} | StringsSG`}</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no"/>
        <meta name="description" content={description}/>
        <meta name="keywords" content="House Rental, Insurance Plan, Personal Trainer, Maid Service, Handyman Service, Aircon Service, Savings Plan, Food Catering, Interior Design, Wedding Planner, Event Planner"/>
        <meta name="author" content="StringsSG"/>
        {!isIndexable && <meta name="robots" content="noindex, follow"/>}
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:url" content={"https://www.stringssg.com/profile/" + stringHandleName + "/reviews"} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`Reviews for ${about.userFirstName} ${about.userLastName} | ${about.category} | StringsSG`} />
        <meta property="og:description" content={about.description}/>
        <meta property="og:image"  content={profilePicture} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={profilePicture} />
        <meta name="twitter:site" content="@stringssg"/> 
        <meta name="twitter:url" content={"https://www.stringssg.com/profile/" + stringHandleName + "/reviews"}/>
        <meta name="twitter:title" content={`Reviews for ${about.userFirstName} ${about.userLastName} | ${about.category} | StringsSG`}/> 
        <meta name="twitter:description" content={about.description}/>
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href={"https://www.stringssg.com/profile/" + stringHandleName + "/reviews"} />
        {/* Built with JSON.stringify: names are user input, and a quote in one
            used to break the string-interpolated JSON. */}
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Person",
                "name": fullName,
                "url": `https://www.stringssg.com/profile/${stringHandleName}`,
                "image": profilePicture,
                "jobTitle": about.category,
                "worksFor": { "@type": "Organization", "@id": "https://www.stringssg.com/#organization", "name": "StringsSG" }
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": fullName, "item": `https://www.stringssg.com/profile/${stringHandleName}` },
                  { "@type": "ListItem", "position": 2, "name": "Reviews", "item": `https://www.stringssg.com/profile/${stringHandleName}/reviews` }
                ]
              }
            ]
          }) }}
        />
      </Head>  
        <div className="max-w-4xl mx-auto overflow-hidden"> 
            <Review reviews={reviews} about={about} profilePicture={profilePicture} stringHandleName={stringHandleName} stringsUserId={stringsUserId}/>
        </div>
        <Script 
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {              
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
          
            gtag('config', 'G-QD1HGGHDYJ');
          }} 
        />
        <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
					  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
					  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
					  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
					  n.queue=[];t=b.createElement(e);t.async=!0;
					  t.src=v;s=b.getElementsByTagName(e)[0];
					  s.parentNode.insertBefore(t,s)}(window, document,'script',
					  'https://connect.facebook.net/en_US/fbevents.js');
					  fbq('init', '128634041116395');
					  fbq('track', 'PageView');`}
        </Script>
    </>
  )
}